import { useAppSelector } from '../redux/app';
import { selectPositionProfit, selectIsCalculating } from '../redux/slices/positionProfitsSlice';

/**
 * Helper function to generate position ID consistently
 */
const getPositionId = (position) => {
  if (!position) return null;
  return `${position.symbol}_${position.priceOpen}_${position.action}_${position.volume}_${position.position}`;
};

/**
 * Custom hook to get position profit from the background calculator
 *
 * @param {Object} position - Position object with symbol, priceOpen, action, volume
 * @returns {Object} { profit: number, calculatedBy: string, timestamp: number, isCalculating: boolean }
 */
export const usePositionProfit = (position) => {
  const positionId = getPositionId(position);

  // Get calculated profit from Redux store
  const calculatedProfit = useAppSelector((state) =>
    positionId ? selectPositionProfit(state, positionId) : null
  );

  const positionNormal = useAppSelector((state) => state.positions?.positions);

  // Get calculation status
  const isCalculating = useAppSelector(selectIsCalculating);

  return {
    profit: calculatedProfit?.profit || 0,
    swap: positionNormal?.find((p) => p.symbol === position.symbol)?.swap || 0,
    calculatedBy: calculatedProfit?.calculatedBy || 'Not Available',
    timestamp: calculatedProfit?.timestamp || null,
    isCalculating,
    isAvailable: !!calculatedProfit,
    positionId
  };
};

/**
 * Hook to get all position profits
 */
export const useAllPositionProfits = () => {
  const allProfits = useAppSelector((state) => state.positionProfits?.profits || {});
  const isCalculating = useAppSelector(selectIsCalculating);

  return {
    profits: allProfits,
    isCalculating,
    profitCount: Object.keys(allProfits).length
  };
};

export default usePositionProfit;
